// Per-restaurant editor for every menu_item — title, section,
// category, subcategory. Built 2026-05-24 after the audit pass found
// 17 mis-classified items across 6 restaurants whose Cleanup queues
// were all empty (Cleanup only surfaces 'unknown' rows; the bigger
// class of bug is the LLM picking the WRONG non-unknown value).
//
// Layout: a single dropdown picks the restaurant; the table below
// shows every item grouped by section, with inline-editable section
// (text) + subcategory (dropdown). Title + category-vs-subcategory
// consistency are handled by the backend PATCH endpoint.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import {
  listAdminRestaurants,
  listEditableMenuItems,
  patchMenuItem,
  type EditableMenuItem,
  type ItemSubcategory,
} from '@/api/admin';
import { ApiError } from '@/api/client';

const SUBCATEGORIES: ItemSubcategory[] = [
  'soup',
  'appetizer',
  'main',
  'salad',
  'side',
  'dessert',
  'coffee_tea',
  'cold_drink',
  'alcoholic',
];

export function EditMenuItemsPage() {
  const [slug, setSlug] = useState<string>('');
  const queryClient = useQueryClient();

  const restaurantsQuery = useQuery({
    queryKey: ['admin', 'restaurants-list'],
    queryFn: ({ signal }) => listAdminRestaurants(signal),
  });

  const itemsQuery = useQuery({
    queryKey: ['admin', 'editable-menu-items', slug],
    queryFn: ({ signal }) => listEditableMenuItems(slug, signal),
    enabled: slug.length > 0,
  });

  const patchMutation = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: Parameters<typeof patchMenuItem>[1];
    }) => patchMenuItem(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'editable-menu-items', slug],
      });
    },
  });

  // Filter to graded restaurants (have ≥1 menu_item) so the dropdown
  // isn't a 4000-row scroll of POIs the admin can't edit anyway.
  const restaurantOptions = useMemo(() => {
    const all = restaurantsQuery.data?.restaurants ?? [];
    return all
      .filter((r) => r.total_items > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [restaurantsQuery.data]);

  return (
    <AdminShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Menu items · edit
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Per-restaurant edit surface. Fix LLM mis-extractions (wrong
          section, wrong subcategory) the Cleanup page can't surface
          because it only shows <code>unknown</code> rows.
        </p>
        <div className="mt-4">
          <label htmlFor="restaurant-picker" className="mr-2 text-sm">
            Restaurant:
          </label>
          <select
            id="restaurant-picker"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">— pick a restaurant —</option>
            {restaurantOptions.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.name} ({r.total_items})
              </option>
            ))}
          </select>
        </div>
      </header>

      {itemsQuery.isLoading && slug ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : itemsQuery.error ? (
        <p className="text-sm text-red-600">
          Could not load items: {(itemsQuery.error as ApiError).message}
        </p>
      ) : itemsQuery.data ? (
        <ItemsTable
          items={itemsQuery.data.items}
          onPatch={(id, patch) => patchMutation.mutate({ id, patch })}
          isPatching={patchMutation.isPending}
        />
      ) : null}
    </AdminShell>
  );
}

interface ItemsTableProps {
  items: EditableMenuItem[];
  onPatch: (id: number, patch: Parameters<typeof patchMenuItem>[1]) => void;
  isPatching: boolean;
}

function ItemsTable({ items, onPatch, isPatching }: ItemsTableProps) {
  // Group by section so the admin scans the menu the way users see it.
  const bySection = useMemo(() => {
    const map = new Map<string, EditableMenuItem[]>();
    for (const it of items) {
      const key = it.section ?? '(no section)';
      const arr = map.get(key) ?? [];
      arr.push(it);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (items.length === 0) {
    return <p className="text-sm text-slate-500">No items.</p>;
  }

  return (
    <div className="space-y-6">
      {bySection.map(([section, sectionItems]) => (
        <section key={section}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-600">
            {section}
            <span className="ml-2 text-xs font-normal text-slate-400">
              ({sectionItems.length})
            </span>
          </h2>
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="w-12 py-2 pr-2">id</th>
                <th className="py-2 pr-2">title</th>
                <th className="w-64 py-2 pr-2">section</th>
                <th className="w-32 py-2 pr-2">subcategory</th>
                <th className="w-20 py-2 text-right">save</th>
              </tr>
            </thead>
            <tbody>
              {sectionItems.map((it) => (
                <ItemRow
                  key={it.id}
                  item={it}
                  onPatch={onPatch}
                  disabled={isPatching}
                />
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

interface ItemRowProps {
  item: EditableMenuItem;
  onPatch: (id: number, patch: Parameters<typeof patchMenuItem>[1]) => void;
  disabled: boolean;
}

function ItemRow({ item, onPatch, disabled }: ItemRowProps) {
  // Local-state-per-row pattern. The row tracks its own edits until
  // the admin clicks Save — the row turns dirty when any field
  // differs from the server's last-known values.
  const [section, setSection] = useState(item.section ?? '');
  const [sub, setSub] = useState(item.item_subcategory);

  const dirty =
    section !== (item.section ?? '') || sub !== item.item_subcategory;

  function save() {
    const patch: Parameters<typeof patchMenuItem>[1] = {};
    if (section !== (item.section ?? '')) {
      patch.section = section.length > 0 ? section : null;
    }
    if (sub !== item.item_subcategory) {
      patch.item_subcategory = sub as ItemSubcategory;
    }
    if (Object.keys(patch).length === 0) return;
    onPatch(item.id, patch);
  }

  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 pr-2 text-xs text-slate-400">{item.id}</td>
      <td className="py-2 pr-2 text-slate-800">{item.title}</td>
      <td className="py-2 pr-2">
        <input
          type="text"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
        />
      </td>
      <td className="py-2 pr-2">
        <select
          value={sub}
          onChange={(e) => setSub(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
        >
          {SUBCATEGORIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          {/* unknown stays selectable if the row started as unknown
              (allows reverting an edit without leaving the page) */}
          {item.item_subcategory === 'unknown' && (
            <option value="unknown">unknown</option>
          )}
        </select>
      </td>
      <td className="py-2 text-right">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || disabled}
          className="rounded bg-accent px-3 py-1 text-xs font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
          Save
        </button>
      </td>
    </tr>
  );
}
